/**
 * The propose form's small shared pieces: the labelled field wrapper, and the two-choice toggle
 * that replaced two of its dropdowns.
 *
 * Kept out of `ProposeRoleModal.tsx` because the bulk step uses them too — and because that file
 * is close enough to the 500-line ceiling that the next addition would have gone over it.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import { LABEL_CLASS } from './proposeRole';

/**
 * A labelled field.
 *
 * `htmlFor` points at a real control. Where there is none to point at — the toggle below is a
 * group of buttons, not an input — pass `labelId` instead and give the group
 * `aria-labelledby={labelId}`: a `<label for>` aimed at nothing is worse than no label at all,
 * because a screen reader reports it as a field that cannot be reached.
 */
export function Field({
  label,
  htmlFor,
  labelId,
  error,
  required,
  children,
}: {
  label: string;
  htmlFor?: string;
  labelId?: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  const text = (
    <>
      {label}
      {required && <span className="ml-0.5 text-red-500">*</span>}
    </>
  );
  return (
    <div>
      {htmlFor ? (
        <label className={LABEL_CLASS} htmlFor={htmlFor}>
          {text}
        </label>
      ) : (
        <span className={LABEL_CLASS} id={labelId}>
          {text}
        </span>
      )}
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

/**
 * A single-choice toggle over a short vocabulary, shaped like the Select all / Select none strip
 * in the filter dropdowns (David, Sep 2026).
 *
 * Reason for Hiring and Cost Category each carry exactly two usable choices — New Hire/Backfill
 * (CAC is hidden, see `HIDDEN_CHOICES`) and OpEx/CAC — so a dropdown made someone open a menu to
 * read two words and pick one of them. Both options are on screen here, and which one is set is
 * visible without interacting at all.
 *
 * Clicking the active choice CLEARS it: both fields are optional on the Airtable side, and a
 * toggle you cannot un-set would make "I have not decided" unreachable once touched.
 *
 * It renders whatever vocabulary Airtable returns rather than a hardcoded pair — the options are
 * read live from the schema, so a third choice added there must show up rather than disappear.
 * `unavailable` is the schema-read failure the form already handles: the strip renders disabled
 * so the field still reads as a field.
 */
export function ChoiceToggle({
  labelledBy,
  options,
  value,
  onChange,
  disabled,
}: {
  labelledBy: string;
  options: string[];
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  if (options.length === 0) {
    return (
      <p className="py-1.5 text-sm text-zinc-400">{disabled ? 'Unavailable' : 'No choices offered'}</p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-labelledby={labelledBy}>
      {options.map((choice) => {
        const selected = value === choice;
        return (
          <Button
            key={choice}
            type="button"
            size="sm"
            variant={selected ? 'primary' : 'outline'}
            aria-pressed={selected}
            disabled={disabled}
            onClick={() => onChange(selected ? '' : choice)}
          >
            {choice}
          </Button>
        );
      })}
    </div>
  );
}
